import React, { useState } from 'react';
import {
    Box,
    Grid,
    Typography,
    Button,
    Card,
    CardContent,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { allIncidentType, northAmericaCountries, CanadaProvinces, USAStates, MexicoStates } from '../../utils/dropOptions';

function Accidents({ onNext }) {
    const [accidents, setAccidents] = useState([{
        accidentDate: null,
        accidentType: '',
        hazmatInvolved: false,
        towedVehicles: false,
        country: '',
        provinceOrState: '',
        commercialVehicleInvolved: false,
        atFault: false,
        receivedTicket: false,
        address: {
            line1: '',
            line2: '',
            city: '',
            stateProvince: '',
            postalCode: '',
            country: '',
        }
    }]);

    const getStateOptions = (country) => {
        switch (country) {
            case 'Canada':
                return CanadaProvinces;
            case 'U.S.A':
                return USAStates;
            case 'Mexico':
                return MexicoStates;
            default:
                return [];
        }
    };

    const handleChange = (index) => (field) => (event) => {
        const newAccidents = [...accidents];
        newAccidents[index] = {
            ...newAccidents[index],
            [field]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
            // Reset province/state when country changes
            ...(field === 'country' ? { provinceOrState: '' } : {})
        };
        setAccidents(newAccidents);
    };

    const handleDateChange = (index) => (date) => {
        const newAccidents = [...accidents];
        newAccidents[index] = {
            ...newAccidents[index],
            accidentDate: date
        };
        setAccidents(newAccidents);
    };

    const handleAddressFieldChange = (index) => (field, value) => {
        const newAccidents = [...accidents];
        newAccidents[index] = {
            ...newAccidents[index],
            address: {
                ...newAccidents[index].address,
                [field]: value
            }
        };
        setAccidents(newAccidents);
    };

    const addAccident = () => {
        setAccidents([...accidents, {
            accidentDate: null,
            accidentType: '',
            hazmatInvolved: false,
            towedVehicles: false,
            country: '',
            provinceOrState: '',
            commercialVehicleInvolved: false,
            atFault: false,
            receivedTicket: false,
            address: {
                line1: '',
                line2: '',
                city: '',
                stateProvince: '',
                postalCode: '',
                country: '',
            }
        }]);
    };

    const removeAccident = (index) => {
        if (accidents.length > 1) {
            setAccidents(accidents.filter((_, i) => i !== index));
        }
    };

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto', p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Accidents (Last 5 Years)</Typography>
                <Button
                    variant="outlined"
                    onClick={addAccident}
                    startIcon={<AddIcon />}
                >
                    Add Accident
                </Button>
            </Box>

            {accidents.map((accident, index) => (
                <Card variant="outlined" sx={{ mb: 3 }} key={index}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="subtitle1">Accident {index + 1}</Typography>
                            {accidents.length > 1 && (
                                <IconButton onClick={() => removeAccident(index)} size="small">
                                    <DeleteIcon />
                                </IconButton>
                            )}
                        </Box>

                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label="Accident Date"
                                        value={accident.accidentDate}
                                        onChange={handleDateChange(index)}
                                        renderInput={(params) => <TextField {...params} fullWidth required />}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <Select
                                        value={accident.accidentType}
                                        onChange={handleChange(index)('accidentType')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select Accident Type</MenuItem>
                                        {allIncidentType.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type.replace(/_/g, ' ').toUpperCase()}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required>
                                    <Select
                                        value={accident.country}
                                        onChange={handleChange(index)('country')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select Country</MenuItem>
                                        {northAmericaCountries.map((country) => (
                                            <MenuItem key={country} value={country}>
                                                {country}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required disabled={!accident.country}>
                                    <Select
                                        value={accident.provinceOrState}
                                        onChange={handleChange(index)('provinceOrState')}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select State/Province</MenuItem>
                                        {getStateOptions(accident.country).map((state) => (
                                            <MenuItem key={state} value={state}>
                                                {state}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accident.hazmatInvolved}
                                            onChange={handleChange(index)('hazmatInvolved')}
                                        />
                                    }
                                    label="Hazmat Materials Involved"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accident.towedVehicles}
                                            onChange={handleChange(index)('towedVehicles')}
                                        />
                                    }
                                    label="Vehicles Were Towed"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accident.commercialVehicleInvolved}
                                            onChange={handleChange(index)('commercialVehicleInvolved')}
                                        />
                                    }
                                    label="Commercial Vehicle Involved"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accident.atFault}
                                            onChange={handleChange(index)('atFault')}
                                        />
                                    }
                                    label="At Fault"
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={accident.receivedTicket}
                                            onChange={handleChange(index)('receivedTicket')}
                                        />
                                    }
                                    label="Received Ticket"
                                />
                            </Grid>


                        </Grid>
                    </CardContent>
                </Card>
            ))}

            <Box sx={{ mt: 3 }}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => onNext(accidents)}
                >
                    Continue
                </Button>
            </Box>
        </Box>
    );
}

export default Accidents;
